import {
    FC,
    ReactNode,
    cloneElement,
    ReactElement,
    CSSProperties,
    useRef,
    useEffect,
} from "react";

export type DropdownDirection =
    | "bottom"
    | "top"
    | "left"
    | "right"
    | "bottom-left"
    | "bottom-right"
    | "top-left"
    | "top-right";

interface DropdownProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    trigger: ReactElement;
    children: ReactNode;
    direction?: DropdownDirection;
}

const getDropdownStyle = (direction: DropdownDirection): CSSProperties => {
    switch (direction) {
        case "top":
            return { position: "absolute", bottom: "100%", left: 0 };
        case "top-left":
            return { position: "absolute", bottom: "100%", left: 0 };
        case "top-right":
            return { position: "absolute", bottom: "100%", right: 0 };
        case "bottom":
            return { position: "absolute", top: "100%", left: '-100%' };
        case "bottom-left":
            return { position: "absolute", top: "100%", left: 0 };
        case "bottom-right":
            return { position: "absolute", top: "100%", right: 0 };
        case "left":
            return { position: "absolute", top: 0, right: "100%" };
        case "right":
            return { position: "absolute", top: 0, left: "100%" };
        default:
            return { position: "absolute", top: "100%", left: 0 };
    }
};

const Dropdown: FC<DropdownProps> = ({
    open,
    setOpen,
    trigger,
    children,
    direction = "bottom",
}) => {
    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleToggle = () => setOpen(!open);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [open, setOpen]);

    const triggerWithProps = cloneElement(trigger, {
        onClick: (e: React.MouseEvent) => {
            trigger.props.onClick?.(e);
            handleToggle();
        },
    });

    return (
        <div
            ref={dropdownRef}
            style={{ position: "relative", display: "inline-block" }}
        >
            {triggerWithProps}
            {open && (
                <div
                    style={{
                        ...getDropdownStyle(direction),
                        background: "var(--surface-elevated)",
                        color: "var(--text-primary)",
                        border: "1px solid var(--border-subtle)",
                        boxShadow: "0 2px 8px var(--shadow-color)",
                        zIndex: 1000,
                        borderRadius: "12px",
                        marginTop: "6px",
                        width: "fit-content",
                    }}
                >
                    {children}
                </div>
            )}
        </div>
    );
};

export default Dropdown;
